import * as debug from '../../utils/debug';
import { utils } from '../../utils/utils';
import { Locale } from '../locale/locale';

// The Name of this component.
const COMPONENT_NAME = 'loadingindicator';

/**
 * Loading Indicator defaults
 * @namespace
 * @property {string} [type='circular'] The type of loading indicator (circular, linear, sticky, generative-ai, line).
 * @property {number|undefined} [progress=undefined] Percentage completed (0-100). If not set, indicator is indeterminate.
 * @property {boolean} [percentageVisible=false] Whether to show the percentage text.
 * @property {boolean} [inline=false] Renders smaller variant for inline use within other components.
 * @property {string} [color='default'] Color variant: 'default' or 'destructive'.
 * @property {array|object} [attributes=null] Add extra attributes like id's to the element.
 */
const LOADINGINDICATOR_DEFAULTS = {
  type: 'circular',
  progress: undefined,
  percentageVisible: false,
  inline: false,
  color: 'default',
  attributes: null
};

/**
 * A Loading Indicator notifies the user that a process is in progress.
 * Supports circular (default), linear, sticky, and generative-ai types.
 * @class LoadingIndicator
 * @param {object} element The plugin starting element.
 * @param {object} [settings] The settings to use.
 */
function LoadingIndicator(element, settings) {
  this.settings = utils.mergeSettings(element, settings, LOADINGINDICATOR_DEFAULTS);
  this.element = $(element);
  debug.logTimeStart(COMPONENT_NAME);
  this.init();
  debug.logTimeEnd(COMPONENT_NAME);
}

LoadingIndicator.prototype = {

  /**
   * Initialize the component.
   * @private
   * @returns {object} The component instance.
   */
  init() {
    this.build();
    this.handleEvents();
    return this;
  },

  /**
   * Build the loading indicator markup.
   * @private
   * @returns {void}
   */
  build() {
    const s = this.settings;
    const isDeterminate = s.progress !== undefined && !isNaN(s.progress);

    this.container = $('<div class="loading-indicator-container"></div>');
    this.container.attr({ 'aria-live': 'polite', role: 'status' });

    if (s.type === 'generative-ai') {
      this.container.addClass('generative-ai');
      this.indicator = $(`
        <div class="ai-loading-indicator">
          <div class="bar"></div>
          <div class="bar"></div>
          <div class="bar"></div>
        </div>
      `).appendTo(this.container);
    } else if (s.type === 'line') {
      this.container.addClass('line');
      this.indicator = $(`
        <div class="line-loading-indicator">
          <div class="line-bars">
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
          </div>
        </div>
        <span class="loading-indicator-text">${Locale.translate('Loading')}</span>
      `).appendTo(this.container);
    } else if (s.type === 'linear' || s.type === 'sticky') {
      this.container.addClass(s.type);
      const yOffset = s.type === 'sticky' ? '0' : '12.5%';
      const stateClass = isDeterminate ? 'determinate' : 'indeterminate';
      this.indicator = $(`
        <svg xmlns="http://www.w3.org/2000/svg" class="linear-indicator ${s.type} ${stateClass}">
          <rect width="100%" height="100%" y="${yOffset}" class="circle"></rect>
          <rect width="100%" height="100%" class="progress"></rect>
        </svg>
      `).appendTo(this.container);
    } else {
      // Circular (default)
      const stateClass = isDeterminate ? 'determinate' : 'indeterminate';
      const inlineClass = s.inline ? ' inline' : '';
      const colorClass = s.color === 'destructive' ? ' destructive' : '';
      const inlineSizeClass = s.inline ? ' inline-size' : '';

      this.container.addClass(`circular${colorClass}${inlineSizeClass}`);
      this.indicator = $(`
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" class="circular-indicator ${stateClass}${inlineClass}">
          <circle cx="50" cy="50" r="45" stroke-width="2" class="circle"></circle>
          <circle cx="50" cy="50" r="45" stroke-width="4" class="progress"></circle>
        </svg>
      `).appendTo(this.container);
    }

    // Add percentage text
    if (s.percentageVisible && s.type !== 'sticky' && s.type !== 'generative-ai' && s.type !== 'line') {
      const typeClass = (s.type === 'linear') ? ' linear' : '';
      this.percentageEl = $(`<div class="loading-percentage${typeClass}"><span class="loading-percentage-text">${isDeterminate ? s.progress : 0}</span></div>`);
      if (s.type === 'linear') {
        this.container.prepend(this.percentageEl);
      } else {
        this.container.append(this.percentageEl);
      }
    }

    this.element.append(this.container);

    // Set progress if determinate
    if (isDeterminate) {
      this.updateProgress(s.progress);
    }

    utils.addAttributes(this.container, this, s.attributes);
    utils.addAttributes(this.indicator, this, s.attributes, 'indicator');
  },

  /**
   * Update the progress value.
   * @param {number} value Percentage value (0-100).
   * @returns {void}
   */
  updateProgress(value) {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || !this.container) return;

    const clampedValue = Math.max(0, Math.min(100, numValue));
    this.settings.progress = clampedValue;

    if (this.settings.type === 'circular') {
      // circumference is 2πr(45) => 282.74, or 283
      const offset = 283 - (clampedValue * 2.83);
      this.container.find('.progress').css('stroke-dashoffset', `${offset}px`);
      this.container.find('svg').removeClass('indeterminate').addClass('determinate');
    } else if (this.settings.type === 'linear' || this.settings.type === 'sticky') {
      this.container.find('.progress').css('transform', `scaleX(${clampedValue / 100})`);
      this.container.find('svg').removeClass('indeterminate').addClass('determinate');
    }

    if (this.percentageEl) {
      this.percentageEl.find('.loading-percentage-text').text(clampedValue);
    }

    this.element.trigger('progress.loadingindicator', [{ progress: clampedValue }]);
  },

  /**
   * Stop the loading indicator (hide it).
   * @returns {void}
   */
  stop() {
    if (!this.container) return;
    this.element.trigger('stopped.loadingindicator', [{ elem: this.element, loading: false }]);
    this.container.addClass('is-stopped');
  },

  /**
   * Start/restart the loading indicator.
   * @returns {void}
   */
  start() {
    if (!this.container) return;
    this.element.trigger('started.loadingindicator', [{ elem: this.element, loading: true }]);
    this.container.removeClass('is-stopped');
  },

  /**
   * Handle events.
   * @private
   * @returns {object} The component instance.
   */
  handleEvents() {
    this.element
      .on('start.loadingindicator', () => {
        this.start();
      })
      .on('stop.loadingindicator', () => {
        this.stop();
      })
      .on('updated.loadingindicator', (e, settings) => {
        e.stopPropagation();
        this.updated(settings);
      });

    return this;
  },

  /**
   * Update the component and apply current settings.
   * @param {object} settings The settings to update to.
   * @returns {object} The component instance.
   */
  updated(settings) {
    if (settings) {
      this.settings = utils.mergeSettings(this.element, settings, this.settings);
    }
    this.teardown();
    this.build();
    this.handleEvents();
    return this;
  },

  /**
   * Remove added markup.
   * @private
   * @returns {void}
   */
  teardown() {
    if (this.container) {
      this.container.remove();
      this.container = undefined;
    }
    this.indicator = undefined;
    this.percentageEl = undefined;
  },

  /**
   * Teardown and remove any added markup and events.
   * @returns {void}
   */
  destroy() {
    this.teardown();
    this.element.off('start.loadingindicator stop.loadingindicator updated.loadingindicator');
    $.removeData(this.element[0], COMPONENT_NAME);
  }
};

export { LoadingIndicator, COMPONENT_NAME };
